import React from 'react';
import { Calendar, Shield, CheckCircle, Award } from 'lucide-react';

export const History = () => {
  const timelineEvents = [
    {
      year: "1961",
      title: "The Beginning",
      description: "First reverse mortgage created by a local lender to help a widow stay in her home.",
      icon: "Calendar",
      color: "blue"
    },
    {
      year: "1988",
      title: "Federal Protection",
      description: "Congress authorizes the FHA-insured HECM program, bringing federal oversight and consumer protections.",
      icon: "Shield",
      color: "blue"
    },
    {
      year: "2000s–2010s",
      title: "Major Reforms",
      description: "Significant reforms add enhanced consumer protections, financial assessment requirements, and mandatory HUD counseling.",
      icon: "CheckCircle",
      color: "blue"
    },
    {
      year: "Today",
      title: "Industry Standard",
      description: "One of the most regulated, consumer-protected mortgage products in the U.S., helping thousands of seniors achieve financial security.",
      icon: "Award",
      color: "green"
    }
  ];

  const iconMap = {
    Calendar,
    Shield,
    CheckCircle,
    Award
  };

  return (
    <section id="history" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            The Evolution of Reverse Mortgages
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From a simple solution for one widow to a federally-regulated program 
            protecting thousands of American seniors.
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical Line - Hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-blue-200"></div>

            {/* Timeline Events */}
            <div className="space-y-12">
              {timelineEvents.map((event, index) => {
                const IconComponent = iconMap[event.icon];
                const isEven = index % 2 === 0;
                const isLast = index === timelineEvents.length - 1;

                return (
                  <div 
                    key={event.year}
                    className={`relative ${isEven ? 'md:pr-1/2' : 'md:pl-1/2'}`}
                  >
                    {/* Desktop Layout */}
                    <div className="hidden md:grid md:grid-cols-2 gap-8 items-center">
                      {/* Left Side */}
                      {isEven ? (
                        <>
                          <div className="text-right pr-8">
                            <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-100 hover:border-blue-300 transition-colors">
                              <h3 className="text-3xl font-bold text-blue-600 mb-2">
                                {event.year}
                              </h3>
                              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                                {event.title}
                              </h4>
                              <p className="text-gray-600 leading-relaxed">
                                {event.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-start">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${
                              isLast ? 'bg-green-600' : 'bg-blue-600'
                            }`}>
                              <IconComponent className="w-8 h-8 text-white" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-end">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${
                              isLast ? 'bg-green-600' : 'bg-blue-600'
                            }`}>
                              <IconComponent className="w-8 h-8 text-white" />
                            </div>
                          </div>
                          <div className="text-left pl-8">
                            <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-100 hover:border-blue-300 transition-colors">
                              <h3 className="text-3xl font-bold text-blue-600 mb-2">
                                {event.year}
                              </h3>
                              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                                {event.title}
                              </h4>
                              <p className="text-gray-600 leading-relaxed">
                                {event.description}
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden">
                      <div className="flex items-start space-x-4">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 ${
                          isLast ? 'bg-green-600' : 'bg-blue-600'
                        }`}>
                          <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-100 flex-1">
                          <h3 className="text-2xl font-bold text-blue-600 mb-2">
                            {event.year}
                          </h3>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {event.title}
                          </h4>
                          <p className="text-gray-600 leading-relaxed text-sm">
                            {event.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-blue-50 rounded-xl p-8 border-2 border-blue-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Six Decades of Helping Seniors
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Today's reverse mortgages are the result of continuous improvements and 
            protections built over 60+ years. You can trust that your interests are protected.
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors duration-200"
          >
            Learn How It Can Help You
          </button>
        </div>
      </div>
    </section>
  );
};
